import { ChangeDetectorRef, Component, ElementRef, inject, Renderer2 } from '@angular/core';
import { Router } from '@angular/router';

import { RoutingService, WindowRef } from '@spartacus/core';
import {
  BREAKPOINT,
  BreakpointService,
  HamburgerMenuService,
  NavigationNode,
  NavigationUIComponent,
} from '@spartacus/storefront';

@Component({
    selector: 'dp-navigation-ui',
    templateUrl: './navigation-ui.component.html',
    styleUrls: ['./navigation-ui.component.scss'],
    standalone: false
})
export class DpNavigationUiComponent extends NavigationUIComponent {
  private readonly wasRedirected = false;
  #openNodes: HTMLElement[] = [];

  readonly #routingService = inject(RoutingService);
  readonly #breakpointService = inject(BreakpointService);

  constructor(
    router: Router,
    elemRef: ElementRef,
    hamburgerMenuService: HamburgerMenuService,
    winRef: WindowRef,
    private readonly localRenderer: Renderer2,
    protected changeDetectorRef: ChangeDetectorRef,
  ) {
    super(router, localRenderer, elemRef, hamburgerMenuService, winRef);
  }

  toggleOpenOverride(event: UIEvent, node: NavigationNode): void {
    const target = <HTMLElement>event.currentTarget;
    const listItem = target.closest('li');
    if (this.#isMobile()) {
      if (node.children?.length && !listItem?.classList.contains('is-open')) {
        if (listItem) {
          this.handleMobileOpenNodeToggle(listItem);
        }
      } else {
        this.#navigate(node);
      }
    } else if (node.url) {
      this.localRenderer.removeClass(listItem, 'has-hover');
      const parentListItem = listItem?.closest('.custom-nav--top-item');
      this.localRenderer.removeClass(parentListItem, 'has-hover');
      this.#navigate(node);
    }
  }

  handleMobileOpenNodeToggle(node: HTMLElement): void {
    if (this.#openNodes.includes(node)) {
      this.#openNodes = this.#openNodes.filter((n) => n !== node);
      this.localRenderer.removeClass(node, 'is-open');
    } else if (!this.wasRedirected) {
      this.#openNodes.push(node);
    } else {
      this.#openNodes = [];
    }
    this.updateClassesOnMobile();
  }

  getTabIndex(node: NavigationNode, depth: number): 0 | -1 {
    return this.flyout && (depth === 0 || node.url) ? 0 : -1;
  }

  back(): void {
    if (this.#openNodes[this.#openNodes.length - 1]) {
      this.localRenderer.removeClass(this.#openNodes[this.#openNodes.length - 1], 'is-open');
      this.#openNodes.pop();
      this.updateClassesOnMobile();
    }
  }

  clear(): void {
    this.#openNodes.forEach((node) => {
      this.localRenderer.removeClass(node, 'is-open');
      this.localRenderer.removeClass(node, 'is-opened');
    });
    this.#openNodes = [];
    this.updateClassesOnMobile();
  }

  shouldShowIcon(node: NavigationNode): boolean {
    return this.flyout && this.hasChildren(node);
  }

  hasChildren(node: NavigationNode): boolean {
    return (node?.children?.length ?? 0) > 0;
  }

  isLastLevel(node: NavigationNode): boolean {
    return node?.children?.length ? !node?.children.some((item) => item.children?.length) : true;
  }

  showNested(node: NavigationNode, listItem: HTMLLIElement): boolean {
    return !!node.children?.length && listItem.classList.contains('has-hover');
  }

  private updateClassesOnMobile(): void {
    this.#openNodes.forEach((node, i) => {
      if (i + 1 < this.#openNodes.length) {
        this.localRenderer.addClass(node, 'is-opened');
        this.localRenderer.removeClass(node, 'is-open');
      } else {
        this.localRenderer.removeClass(node, 'is-opened');
        this.localRenderer.addClass(node, 'is-open');
      }
    });

    this.isOpen = this.#openNodes.length > 0;
  }

  #navigate(node: NavigationNode): void {
    if (node.url) {
      this.#routingService.go(node.url);
    }
  }

  #isMobile(): boolean {
    const width = this.winRef?.nativeWindow?.innerWidth || 0;
    const maxSize = this.#breakpointService?.getSize(BREAKPOINT.lg) || 0;
    return this.winRef?.isBrowser() && width < maxSize;
  }
}
